import { useState, useEffect, useCallback, useRef } from "react";
import {
  Receipt, Search, RefreshCw, ChevronLeft, ChevronRight, X,
  TrendingUp, TrendingDown, Wallet, Store,
} from "lucide-react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const LIMIT = 20;

const ACTION_LABELS = {
  register: "Registro", purchase: "Compra", referral: "Referido",
  redeem: "Canje", review: "Reseña", phone_verify: "Verificación tel.",
};
const ACTION_COLORS = {
  register: "bg-blue-100 text-blue-700", purchase: "bg-emerald-100 text-emerald-700",
  referral: "bg-purple-100 text-purple-700", redeem: "bg-red-100 text-red-600",
  review: "bg-amber-100 text-amber-700", phone_verify: "bg-cyan-100 text-cyan-700",
};

// Reporte admin de Qpuntos: total emitido/redimido/saldo de la plataforma, y el
// detalle de cada transacción (usuario, tienda, pedido, fecha, cantidad) — antes
// solo existía la configuración de puntos-por-acción (QPointsManager), no había
// forma de ver quién ganó/gastó puntos ni con qué tienda.
export default function QPointsReport() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ issued: 0, redeemed: 0, outstanding: 0 });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const searchTimeout = useRef(null);

  const fetchReport = useCallback(async (p = 1, overrides = {}) => {
    setLoading(true);
    try {
      const params = {
        page: p, limit: LIMIT,
        search: overrides.search ?? search,
        action: overrides.action ?? actionFilter,
        from: overrides.from ?? from,
        to: overrides.to ?? to,
      };
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const res = await API.get("/qpoints/admin/report", { params });
      const d = res.data?.data ?? {};
      setRows(d.data ?? []);
      setTotal(d.total ?? 0);
      setPages(d.pages ?? 1);
      setTotals(d.totals ?? { issued: 0, redeemed: 0, outstanding: 0 });
      setPage(p);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error cargando el reporte");
    } finally {
      setLoading(false);
    }
  }, [search, actionFilter, from, to]); // eslint-disable-line

  useEffect(() => { fetchReport(1); }, []); // eslint-disable-line

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchReport(1, { search: val }), 350);
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-0 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Receipt size={24} className="text-amber-500" /> Reporte de Qpuntos
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">{total.toLocaleString()} transacciones</p>
        </div>
        <button
          onClick={() => fetchReport(page)}
          className="flex items-center gap-2 px-4 py-2 bg-white border rounded-xl text-gray-600 hover:bg-gray-50 transition text-sm shadow-sm"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualizar
        </button>
      </div>

      {/* Totales de plataforma */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
          <TrendingUp size={20} className="text-emerald-600 shrink-0" />
          <div>
            <p className="text-xl font-bold text-emerald-700">{totals.issued.toLocaleString()}</p>
            <p className="text-xs text-gray-500">Qpuntos emitidos (histórico)</p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <TrendingDown size={20} className="text-red-500 shrink-0" />
          <div>
            <p className="text-xl font-bold text-red-600">{totals.redeemed.toLocaleString()}</p>
            <p className="text-xs text-gray-500">Qpuntos redimidos (histórico)</p>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <Wallet size={20} className="text-amber-600 shrink-0" />
          <div>
            <p className="text-xl font-bold text-amber-700">{totals.outstanding.toLocaleString()}</p>
            <p className="text-xs text-gray-500">Saldo actual de usuarios</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Buscar por nombre o correo del usuario…"
          className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 shadow-sm"
        />
        {search && (
          <button
            onClick={() => { setSearch(""); fetchReport(1, { search: "" }); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={15} />
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); fetchReport(1, { action: e.target.value }); }}
          className="px-3 py-2 rounded-xl text-sm border border-gray-200 bg-white text-gray-700"
        >
          <option value="">Toda acción</option>
          {Object.entries(ACTION_LABELS).map(([k, label]) => (
            <option key={k} value={k}>{label}</option>
          ))}
        </select>
        <input
          type="date"
          value={from}
          onChange={(e) => { setFrom(e.target.value); fetchReport(1, { from: e.target.value }); }}
          className="px-3 py-2 rounded-xl text-sm border border-gray-200 bg-white text-gray-700"
        />
        <span className="text-xs text-gray-400">hasta</span>
        <input
          type="date"
          value={to}
          onChange={(e) => { setTo(e.target.value); fetchReport(1, { to: e.target.value }); }}
          className="px-3 py-2 rounded-xl text-sm border border-gray-200 bg-white text-gray-700"
        />
        {(actionFilter || from || to) && (
          <button
            onClick={() => { setActionFilter(""); setFrom(""); setTo(""); fetchReport(1, { action: "", from: "", to: "" }); }}
            className="text-xs text-gray-400 hover:text-gray-600 underline"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b text-left">
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Usuario</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide hidden sm:table-cell">Tienda</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide hidden md:table-cell">Pedido</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide hidden lg:table-cell">Fecha</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Acción</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide text-right">Qpuntos</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center">
                    <RefreshCw className="animate-spin mx-auto text-gray-300 mb-2" size={24} />
                    <p className="text-gray-400 text-sm">Cargando…</p>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center text-gray-400 text-sm">
                    Sin transacciones con estos filtros
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/50 transition">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-800 truncate max-w-[160px]">{r.user?.name ?? "—"}</div>
                      <div className="text-xs text-gray-400 truncate max-w-[160px]">{r.user?.email ?? ""}</div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      {r.order?.company?.name ? (
                        <span className="flex items-center gap-1.5 text-gray-600">
                          <Store size={12} className="text-gray-400 shrink-0" /> {r.order.company.name}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-xs font-mono text-gray-500">{r.order?.orderNumber ?? "—"}</span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <div className="text-xs text-gray-500">{dayjs(r.createdAt).format("DD MMM YYYY")}</div>
                      <div className="text-xs text-gray-400">{dayjs(r.createdAt).format("HH:mm")}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ACTION_COLORS[r.action] ?? "bg-gray-100 text-gray-600"}`}>
                        {ACTION_LABELS[r.action] ?? r.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-bold ${r.points >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                        {r.points >= 0 ? "+" : ""}{r.points}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <span className="text-xs text-gray-500">
              Página {page} de {pages} · {total.toLocaleString()} transacciones
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => fetchReport(page - 1)}
                disabled={page <= 1 || loading}
                className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(5, pages) }, (_, i) => {
                const p = page <= 3 ? i + 1 : page - 2 + i;
                if (p < 1 || p > pages) return null;
                return (
                  <button
                    key={p}
                    onClick={() => fetchReport(p)}
                    className={`w-7 h-7 rounded-lg text-xs font-semibold transition ${p === page ? "bg-amber-500 text-white" : "hover:bg-gray-200 text-gray-600"}`}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => fetchReport(page + 1)}
                disabled={page >= pages || loading}
                className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
